import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertIcon,
  Avatar,
  Box,
  Button,
  Flex,
  Grid,
  Heading,
  HStack,
  SimpleGrid,
  Skeleton,
  Text,
  VStack,
} from "@chakra-ui/react";
import { MdChevronRight, MdRefresh } from "react-icons/md";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import PageLayoutWithHeaderNav from "../PageLayoutWithHeaderNav";
import { getEngagementDashboard } from "../../services/managerEngagement";
import CoachingPerformanceDashboard from "./CoachingPerformanceDashboard";
import ReportingPeriodControl from "./ReportingPeriodControl";
import { normalizeReportingPeriodId } from "./reportingPeriod";
import { useUser } from "../../contexts/UserContext";

function duration(value) {
  const seconds = Math.max(0, Math.round(Number(value || 0) / 1000));
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

function pluralized(count, noun) {
  const value = Number(count || 0);
  return `${value} ${noun}${value === 1 ? "" : "s"}`;
}

function calendarDate(value) {
  const [year, month, day] = String(value || "").split("-").map(Number);
  if (!year || !month || !day) return null;
  return new Date(Date.UTC(year, month - 1, day));
}

function formatCalendarDate(value, options) {
  const parsed = calendarDate(value);
  if (!parsed) return value || "Unknown";
  return new Intl.DateTimeFormat(undefined, { ...options, timeZone: "UTC" }).format(parsed);
}

function shortDate(value) {
  return formatCalendarDate(value, { month: "short", day: "numeric" });
}

function weekday(value) {
  return formatCalendarDate(value, { weekday: "short" });
}

function Kpi({ label, value, detail }) {
  return (
    <Box bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="xl" p={5} boxShadow="sm">
      <Text fontSize="xs" color="gray.500" fontWeight="bold" textTransform="uppercase" letterSpacing="wide">
        {label}
      </Text>
      <Text mt={2} fontSize="3xl" color="gray.900" fontWeight="bold" sx={{ fontVariantNumeric: "tabular-nums" }}>
        {value}
      </Text>
      {detail && <Text mt={1} fontSize="sm" color="gray.500">{detail}</Text>}
    </Box>
  );
}

function ActivityCell({ day, max, onClick }) {
  const count = Number(day?.thread_count ?? day?.recording_count ?? 0);
  const talkTimeMs = Number(day?.talk_time_ms || 0);
  const opacity = count ? 0.16 + (0.72 * count) / Math.max(1, max) : 0;
  const hasProblem = Number(day?.failed || 0) + Number(day?.empty || 0) > 0;
  const hasActivity = count > 0 || talkTimeMs > 0 || Number(day?.session_count || 0) > 0 || hasProblem;
  return (
    <Box
      as="button"
      type="button"
      aria-label={`${day?.date || "Day"}: ${count} customer conversations`}
      title={`${day?.date || ""}: ${count} customer conversations, ${duration(day?.talk_time_ms)}`}
      onClick={hasActivity ? onClick : undefined}
      disabled={!hasActivity}
      minH="62px"
      px={1}
      py={1.5}
      borderRadius="md"
      borderWidth="1px"
      borderColor={hasProblem ? "red.200" : hasActivity ? "gray.200" : "gray.100"}
      bg={count ? `rgba(37, 99, 235, ${opacity})` : hasActivity ? "gray.50" : "white"}
      color={opacity > 0.5 ? "white" : hasActivity ? "gray.700" : "gray.400"}
      fontSize="xs"
      fontWeight="bold"
      sx={{ fontVariantNumeric: "tabular-nums" }}
      cursor={hasActivity ? "pointer" : "default"}
      _hover={hasActivity ? { borderColor: "blue.400", transform: "translateY(-1px)" } : undefined}
      _disabled={{ opacity: 1 }}
      transition="all 0.12s"
    >
      {hasActivity ? (
        <>
          <Text as="span" display="block" fontSize="10px" lineHeight="shorter" opacity={0.82}>
            {shortDate(day?.date)}
          </Text>
          <Text as="span" display="block" mt={1} fontSize="sm" lineHeight="short" fontWeight="bold">
            {count ? `${count} ${count === 1 ? "conversation" : "conversations"}` : "No activity"}
          </Text>
          {talkTimeMs > 0 && (
            <Text as="span" display="block" mt={0.5} fontSize="10px" lineHeight="shorter" opacity={0.82}>
              {duration(talkTimeMs)}
            </Text>
          )}
        </>
      ) : (
        <Text as="span" display="block" textAlign="left" pl={1} fontSize="10px" lineHeight="shorter" fontWeight="medium">
          {shortDate(day?.date)}
        </Text>
      )}
    </Box>
  );
}

function ActivityHeatmap({ dates, days, onOpen }) {
  const dayMap = useMemo(() => new Map((days || []).map((day) => [day.date, day])), [days]);
  const max = Math.max(1, ...Array.from(dayMap.values()).map((day) => Number(day.thread_count ?? day.recording_count ?? 0)));
  const weeks = [];
  for (let index = 0; index < dates.length; index += 7) weeks.push(dates.slice(index, index + 7));
  const weekdayDates = weeks[0] || [];

  return (
    <Box aria-label="Daily recording activity">
      <Grid templateColumns="70px repeat(7, minmax(0, 1fr))" columnGap={1.5} rowGap={1.5} alignItems="stretch">
        <Box aria-hidden="true" />
        {weekdayDates.map((date) => (
          <Text key={date} textAlign="center" color="gray.500" fontSize="10px" fontWeight="bold" textTransform="uppercase">
            {weekday(date)}
          </Text>
        ))}
        {weeks.map((week) => (
          <React.Fragment key={week[0]}>
            <Flex direction="column" justify="center" minW={0}>
              <Text fontSize="9px" color="gray.400" fontWeight="bold" textTransform="uppercase" lineHeight="shorter">
                Week of
              </Text>
              <Text fontSize="xs" color="gray.600" fontWeight="semibold" lineHeight="short">
                {shortDate(week[0])}
              </Text>
            </Flex>
            {week.map((date) => {
              const day = dayMap.get(date) || { date, recording_count: 0, talk_time_ms: 0 };
              return <ActivityCell key={date} day={day} max={max} onClick={() => onOpen(date)} />;
            })}
          </React.Fragment>
        ))}
      </Grid>
    </Box>
  );
}

function EmployeeRow({ employee, dates, onOpen }) {
  return (
    <Box bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="xl" p={4} boxShadow="sm">
      <Flex gap={4} direction={{ base: "column", lg: "row" }} align={{ lg: "center" }}>
        <HStack minW={{ lg: "250px" }} spacing={3}>
          <Avatar name={employee.name} size="sm" bg="blue.500" color="white" />
          <Box minW={0}>
            <Text fontWeight="bold" color="gray.900" noOfLines={1}>{employee.name}</Text>
            <Text fontSize="xs" color="gray.500">
              {pluralized(employee.thread_count ?? employee.recording_count, "customer conversation")} - {duration(employee.talk_time_ms)}
            </Text>
          </Box>
        </HStack>
        <Box flex="1" minW={0}>
          <ActivityHeatmap dates={dates} days={employee.days} onOpen={(date) => onOpen(employee.id, date)} />
        </Box>
        <Button size="sm" variant="ghost" rightIcon={<MdChevronRight />} onClick={() => onOpen(employee.id)}>
          Review
        </Button>
      </Flex>
    </Box>
  );
}

export default function EngagementDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { userInfo } = useUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [periodId, setPeriodId] = useState(normalizeReportingPeriodId(searchParams.get("period")));
  const [jobClass, setJobClass] = useState(searchParams.get("job_class") || "");
  const [frameworkFamily, setFrameworkFamily] = useState(searchParams.get("framework") || "");
  const [reportingGroupId, setReportingGroupId] = useState(searchParams.get("reporting_group") || "");
  const [locationId, setLocationId] = useState(searchParams.get("location") || "");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refreshVersion, setRefreshVersion] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setData(await getEngagementDashboard(periodId || undefined, {
        reportingGroupId: reportingGroupId || undefined,
        locationId: locationId || undefined,
      }));
    } catch (err) {
      setError(err.response?.data?.detail || "Could not load manager engagement.");
    } finally {
      setLoading(false);
    }
  }, [locationId, periodId, reportingGroupId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const section = (location.hash || "").replace("#", "");
    if (!section) return;
    const frame = window.requestAnimationFrame(() => document.getElementById(section)?.scrollIntoView({ behavior: "smooth", block: "start" }));
    return () => window.cancelAnimationFrame(frame);
  }, [location.hash, data]);
  const dates = data?.days?.map((day) => day.date) || [];
  const setSelection = (key, value, setter) => {
    setter(value);
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value); else next.delete(key);
    setSearchParams(next, { replace: true });
  };
  const setJobClassSelection = (value) => {
    setJobClass(value);
    setFrameworkFamily("");
    const next = new URLSearchParams(searchParams);
    if (value) next.set("job_class", value); else next.delete("job_class");
    // Framework families are job-class specific. Keeping the previous role's
    // family (for example finance/gee while switching to sales) produces a
    // valid but empty backend result, so reset both pieces of state atomically.
    next.delete("framework");
    setSearchParams(next, { replace: true });
  };
  const openEmployee = (employeeId, date) => {
    const params = new URLSearchParams();
    if (data?.period?.id) params.set("period", data.period.id);
    if (date) params.set("date", date);
    if (jobClass) params.set("job_class", jobClass);
    if (frameworkFamily) params.set("framework", frameworkFamily);
    if (reportingGroupId) params.set("reporting_group", reportingGroupId);
    if (locationId) params.set("location", locationId);
    navigate(`/manager/employees/${employeeId}/engagement?${params.toString()}`);
  };
  const refreshAll = () => {
    load();
    setRefreshVersion((current) => current + 1);
  };
  return (
    <PageLayoutWithHeaderNav maxWidth="container.xl">
      <VStack align="stretch" spacing={8}>
        <Flex id="overview" scrollMarginTop="24px" justify="space-between" align={{ base: "flex-start", md: "center" }} gap={4} direction={{ base: "column", md: "row" }}>
          <Box>
            <Heading size="xl" fontSize={{ base: "3xl", md: "4xl" }} letterSpacing="-0.025em">Welcome back, {userInfo?.first_name || "Manager"}</Heading>
            <Text mt={2} color="gray.600">
              Here is how your team is performing across {data?.period?.label?.toLowerCase() || "the current period"}.
            </Text>
          </Box>
          <HStack align="end" spacing={2} wrap="wrap">
            <ReportingPeriodControl
              period={data?.period}
              periodId={periodId}
              onChange={(value) => setSelection("period", value, setPeriodId)}
            />
            <Button leftIcon={<MdRefresh />} variant="outline" size="sm" onClick={refreshAll} isLoading={loading}>Refresh</Button>
          </HStack>
        </Flex>

        {error && <Alert status="error" borderRadius="lg"><AlertIcon />{error}</Alert>}
        <CoachingPerformanceDashboard
          periodId={periodId || undefined}
          jobClass={jobClass || undefined}
          frameworkFamily={frameworkFamily || undefined}
          reportingGroupId={reportingGroupId || undefined}
          locationId={locationId || undefined}
          onJobClassChange={setJobClassSelection}
          onFrameworkFamilyChange={(value) => setSelection("framework", value, setFrameworkFamily)}
          onReportingGroupChange={(value) => setSelection("reporting_group", value, setReportingGroupId)}
          onLocationChange={(value) => setSelection("location", value, setLocationId)}
          refreshVersion={refreshVersion}
        />
        {loading && !data ? (
          <SimpleGrid columns={{ base: 1, md: 4 }} spacing={4}>{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} h="130px" borderRadius="xl" />)}</SimpleGrid>
        ) : data ? (
          <Box id="activity" scrollMarginTop="80px">
            <Box mb={4}>
              <Heading size="lg">Activity & processing</Heading>
              <Text color="gray.500" mt={1}>Recording coverage and workflow health for the same authorized team scope.</Text>
            </Box>
            <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={4}>
              <Kpi label="Visible advisors" value={data.summary.visible_employee_count} detail={`${data.summary.active_employee_count} active`} />
              <Kpi label="Customer conversations" value={data.summary.thread_count ?? data.summary.recording_count} detail={`${data.summary.recording_count} recordings`} />
              <Kpi label="Captured talk time" value={duration(data.summary.talk_time_ms)} detail={`${duration(data.summary.average_recording_ms)} average recording`} />
              <Kpi label="Reports ready" value={data.summary.ready} detail={`${data.summary.failed} failed · ${data.summary.processing} processing`} />
            </SimpleGrid>

            <Box mt={7}>
              <Flex justify="space-between" align="end" mb={3} gap={4} wrap="wrap">
                <Box>
                  <Heading size="md">Advisor activity</Heading>
                  <Text fontSize="sm" color="gray.500" mt={1}>Each active day shows customer conversations and captured talk time. Darker blue means more conversations; a red outline means a conversation needs attention.</Text>
                </Box>
              </Flex>
              <VStack align="stretch" spacing={3}>
                {data.employees.map((employee) => <EmployeeRow key={employee.id} employee={employee} dates={dates} onOpen={openEmployee} />)}
                {!data.employees.length && (
                  <Box p={10} bg="white" borderWidth="1px" borderRadius="xl" textAlign="center">
                    <Heading size="sm">No advisors in your reporting groups</Heading>
                    <Text color="gray.500" mt={2}>An administrator can add employees to a reporting group assigned to you.</Text>
                  </Box>
                )}
              </VStack>
            </Box>
          </Box>
        ) : null}
      </VStack>
    </PageLayoutWithHeaderNav>
  );
}
